/**
 * `mockApplicationFormClient` — in-memory implementation of
 * `ApplicationFormClient` for the mock-first dev build (application-form-builder
 * spec, task 19.2). Backed by clones of seed fixtures; mutations are applied to
 * the in-memory store and reflected on subsequent reads, so every
 * Application_Form surface (onboarding, builder, renderer, review, judge
 * projection) is fully clickable with no backend.
 *
 * The decisive design constraint is that mock behavior MUST match the server.
 * We guarantee that the same way `deriveZodSchema.ts` (task 20.1) does: by NOT
 * re-implementing any decision here, and instead delegating every rule to the
 * very same I/O-free pure modules the `application-form-service` Lambda runs:
 *
 *   - validation       → `fieldValidation.validateResponse`
 *   - versioning        → `formVersion.snapshotForm` / `immutabilityGuard` /
 *                         `renderResponse`
 *   - bound-field rules → `boundField.*` (`divisionSelectorOptions`,
 *                         `validateDivisionSelection`, `assertNoBoundValues`,
 *                         `resolveBoundField`, `writerForTarget`)
 *   - onboarding routing→ `onboardingRouting.resolveDestination`
 *   - document gate     → `documentCatalog.missingRequiredDocuments`
 *   - title contention  → `titlePreferences.titleContentionWarnings`
 *   - page approval     → `pageApproval.applyTransition`
 *   - judge projection  → `audienceVisibility.projectForJudge`
 *   - structure rules   → `formStructure.*`
 *
 * Each of those modules is reached by a direct relative cross-import into the
 * infrastructure package — the SAME working mechanism `deriveZodSchema.ts`
 * settled on (the modules are I/O-free: they only pull the pure `FieldType` /
 * `Audience` vocabulary from `infrastructure/types/database.ts` plus the pure
 * `division-registry` logic, so they bundle cleanly under Vite/Vitest). No AWS
 * SDK and no Node-only dependency is dragged in (in particular this file does
 * NOT import `contactIntake.ts`, whose `personLogic` dependency reaches for
 * `crypto`), keeping the mock browser-safe.
 *
 * The transport DTOs in `./types` are re-declared independently of the backend
 * pure-module types but are structurally identical; the only nominal gap is the
 * `FieldType` string-enum (backend) vs string-union (transport), bridged with a
 * single localized cast where a snapshot crosses into a pure function.
 *
 * Validates: application-form-builder Requirements 5.2, 6.1, 8.1, 9.1, 15.1, 17.2
 */

import type { ApplicationFormClient } from './applicationFormClient';
import type {
  ApplicationForm,
  ApplicationResponse,
  ApplicationResponseSummary,
  ApplicationReviewDecision,
  ApplicationSection,
  ApprovalDetailVisibilityConfig,
  ApprovalDetailVisibilityInput,
  ContactIntakeInput,
  CreateFormFieldInput,
  CreateFormPageInput,
  CreateDocumentReferenceInput,
  Destination,
  DocumentCatalogEntry,
  DocumentCatalogEntryInput,
  DocumentRef,
  DocumentType,
  GetDocumentUploadUrlInput,
  FormField,
  FormPage,
  FormVersion,
  FormVersionSnapshot,
  ManualLockOverride,
  PageApproval,
  PresignedUpload,
  RenderedResponse,
  ResponseValue,
  ResponseValues,
  ResponseValuesRow,
  SaveDraftInput,
  SetPageApprovalInput,
  SubmitInput,
  SubmitResult,
  Title,
  UpdateFormFieldInput,
  UpdateFormPageInput,
} from './types';

// --- Shared, I/O-free pure modules (the server runs these exact functions) ---
import { validateResponse } from '../../../../../infrastructure/lambda/application-form/fieldValidation';
import {
  immutabilityGuard,
  renderResponse,
  snapshotForm,
} from '../../../../../infrastructure/lambda/application-form/formVersion';
import {
  assertNoBoundValues,
  resolveBoundField,
  validateDivisionSelection,
} from '../../../../../infrastructure/lambda/application-form/boundField';
import { resolveDestination } from '../../../../../infrastructure/lambda/application-form/onboardingRouting';
import { missingRequiredDocuments } from '../../../../../infrastructure/lambda/application-form/documentCatalog';
import {
  normalizeRanked,
  titleContentionWarnings,
} from '../../../../../infrastructure/lambda/application-form/titlePreferences';
import {
  applyTransition,
  DEFAULT_PAGE_APPROVAL_STATUS,
} from '../../../../../infrastructure/lambda/application-form/pageApproval';
import { projectForJudge } from '../../../../../infrastructure/lambda/application-form/audienceVisibility';
import { evaluatePhaseState } from '../../../../../infrastructure/lambda/application-form/phaseDeadlines';
import {
  routeDecisionNote,
  toOwnerReadModel,
} from '../../../../../infrastructure/lambda/application-form/decisionNotes';
import type { SubmissionStatus as PureSubmissionStatus } from '../../../../../infrastructure/types/database';
import {
  nextDisplayOrder,
  normalizeDisplayOrder,
  validateFieldLabel,
  validateFieldType,
  validatePageTitle,
} from '../../../../../infrastructure/lambda/application-form/formStructure';

// --- Pure-module types, used only to bridge into the pure functions above ----
import type { FormVersionSnapshot as PureSnapshot } from '../../../../../infrastructure/lambda/application-form/fieldValidation';
import type {
  FormFieldRow,
  PageRow,
} from '../../../../../infrastructure/lambda/application-form/formVersion';
import type { CanonicalSources as BoundCanonicalSources } from '../../../../../infrastructure/lambda/application-form/boundField';
import type { CanonicalSources as JudgeCanonicalSources } from '../../../../../infrastructure/lambda/application-form/audienceVisibility';
import type { ApprovalDetails } from '../../../../../infrastructure/lambda/application-form/pageApproval';
import type { DocumentCatalogEntry as PureCatalogEntry } from '../../../../../infrastructure/lambda/application-form/documentCatalog';
import type { RoutingState } from '../../../../../infrastructure/lambda/application-form/onboardingRouting';
import type { ApplicantType } from '../../../../../infrastructure/lambda/division-registry/resolution';
import type { Division } from '../../../../../infrastructure/lambda/division-registry/registryLogic';

// ===========================================================================
// Small utilities
// ===========================================================================

/** Structured-clone fallback that works in jsdom + node test envs. */
function clone<T>(value: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(value)
    : (JSON.parse(JSON.stringify(value)) as T);
}

/** Simulated transport latency (ms). Kept at 0 so tests stay fast. */
const LATENCY_MS = 0;

/** Resolve a fresh deep clone so callers can never mutate the store by reference. */
function delay<T>(value: T): Promise<T> {
  if (LATENCY_MS <= 0) return Promise.resolve(clone(value));
  return new Promise((resolve) => setTimeout(() => resolve(clone(value)), LATENCY_MS));
}

/** Mint a stable-ish unique id for created entities. */
function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

const nowIso = (): string => new Date().toISOString();

// ===========================================================================
// Store-internal canonical-entity shapes (Bound_Fields resolve from these)
// ===========================================================================

interface PersonContact {
  legalFirstName: string;
  legalLastName: string;
  primaryEmail: string;
  mobilePhone?: string;
  dateOfBirth?: string;
  address?: { street: string; city: string; state: string; zip: string };
  profilePhotoS3Key?: string | null;
}

interface ParticipationRec {
  divisionKey?: string;
  competitionYear?: number;
  approvedTitle?: string;
  titlePreferences: string[];
}

interface UploadedDoc {
  /** The DocumentReference id (the delete key; apply-document-uploads Req 6.1). */
  id: string;
  catalogEntryId: string;
  /** The uploaded file's document classification (Req 2.3). */
  documentType: DocumentType;
  /** The competition year the upload belongs to (the GSI sort key, Req 7.1). */
  competitionYear: number;
  fileName: string;
  s3Key: string;
  contentType: string;
  /** ISO-8601 capture time (Req 2.3). */
  uploadedAt: string;
}

// ===========================================================================
// Seed identifiers
// ===========================================================================

/** The single seeded competition the mock surfaces operate against. */
export const MOCK_COMPETITION_ID = 'comp-app-form-mock-2026';
const MOCK_COMPETITION_YEAR = 2026;
const MOCK_FORM_SCHEMA_ID = 'form-app-form-mock';

/** The mock "current applicant" — the identity every applicant-facing method acts as. */
export const MOCK_APPLICANT_PERSON_ID = 'person-applicant-mock';
/** The applicant type the mock applicant is treated as for document-catalog scope. */
const MOCK_APPLICANT_TYPE: ApplicantType = 'MISS';

/** A second seeded applicant, present only to populate the director review list. */
const MOCK_OTHER_APPLICANT_PERSON_ID = 'person-applicant-other';

// ===========================================================================
// The in-memory store
// ===========================================================================

/**
 * In-memory response shape: the transport `ApplicationResponse` DTO plus the
 * director-only `internalRejectNote`. That note is deliberately absent from the
 * owner-selectable `ApplicationResponse` DTO (the structural R5 guarantee), but
 * the mock store must still persist it so the director review envelope can
 * surface it and the owner read can strip it — mirroring how the server keeps
 * the note on `ApplicationResponseItem` yet omits it from the owner read model.
 */
type StoredApplicationResponse = ApplicationResponse & {
  internalRejectNote?: string;
};

interface MockStore {
  competitionId: string;
  competitionYear: number;
  /** The application-selectable Division_Registry the bound Division control reads. */
  divisions: Division[];
  /** The season-scoped Title_Catalog the contestant/director selection surfaces read. */
  titles: Title[];
  form: ApplicationForm;
  pages: FormPage[];
  fields: FormField[];
  documentCatalog: DocumentCatalogEntry[];
  /** Published Form_Versions, oldest → newest; the last is the active one. */
  versions: FormVersion[];
  /** Application_Responses, keyed `applicantPersonId#competitionId`. */
  responses: Map<string, StoredApplicationResponse>;
  /** Person contact/identity SSOT, keyed by personId (Contact_Identity source). */
  persons: Map<string, PersonContact>;
  /** Participation facts, keyed `applicantPersonId#competitionId`. */
  participations: Map<string, ParticipationRec>;
  /** Uploaded documents, keyed `applicantPersonId#competitionId`. */
  documents: Map<string, UploadedDoc[]>;
  /** Page approvals, keyed `applicantPersonId#competitionId#formPageId`. */
  pageApprovals: Map<string, PageApproval>;
  /** Judge-visible Approval_Details key config, keyed `competitionId#formPageId` (Req 22.7). */
  approvalDetailVisibility: Map<string, ApprovalDetailVisibilityConfig>;
}

const responseKey = (applicantPersonId: string, competitionId: string): string =>
  `${applicantPersonId}#${competitionId}`;
const approvalKey = (
  applicantPersonId: string,
  competitionId: string,
  formPageId: string,
): string => `${applicantPersonId}#${competitionId}#${formPageId}`;
/** Key for the per-page judge-visible Approval_Details config (Req 22.7). */
const approvalVisibilityKey = (competitionId: string, formPageId: string): string =>
  `${competitionId}#${formPageId}`;

// ===========================================================================
// Seed fixtures
// ===========================================================================

function seedDivisions(): Division[] {
  const base = {
    competitionYear: MOCK_COMPETITION_YEAR,
    appliesToJudging: true,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  };
  return [
    { id: 'div-miss', key: 'miss', label: 'Miss', displayOrder: 1, appliesToApplications: true, active: true, ...base },
    { id: 'div-teen', key: 'teen', label: 'Teen', displayOrder: 2, appliesToApplications: true, active: true, ...base },
    // Inactive + not application-facing: must never be a selectable Division option.
    { id: 'div-retired', key: 'retired', label: 'Retired', displayOrder: 3, appliesToApplications: false, active: false, ...base },
  ];
}

/**
 * Seed the season-scoped Title_Catalog for the mock competition year
 * (season-scoped-title-registry Req 5.1). Ordered by `displayOrder`; includes an
 * inactive entry so consumers exercise the active-filter path the same way the
 * Division seed does. The labels intentionally include "Miss Gem State" — the
 * value the seeded OTHER applicant already holds as an approvedTitle — so the
 * catalog is consistent with the existing approval fixtures.
 */
function seedTitles(): Title[] {
  const base = {
    competitionYear: MOCK_COMPETITION_YEAR,
    active: true,
    applicantType: null,
    divisionKey: null,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  };
  return [
    { id: 'title-gem-state', key: 'miss-gem-state', label: 'Miss Gem State', displayOrder: 1, ...base },
    { id: 'title-boise', key: 'miss-boise', label: 'Miss Boise', displayOrder: 2, ...base },
    { id: 'title-treasure-valley', key: 'miss-treasure-valley', label: 'Miss Treasure Valley', displayOrder: 3, ...base },
    // Inactive: retained in the catalog but never a selectable option.
    { id: 'title-retired', key: 'miss-retired', label: 'Miss Retired', displayOrder: 4, ...base, active: false },
  ];
}

function seedForm(): ApplicationForm {
  return {
    id: MOCK_FORM_SCHEMA_ID,
    formType: 'PHASE1',
    applicantType: 'BOTH',
    competitionYear: MOCK_COMPETITION_YEAR,
    isActive: true,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
    competitionId: MOCK_COMPETITION_ID,
    title: 'Idaho America Pageant Application',
    currentVersionNumber: 1,
  };
}

function seedPages(): FormPage[] {
  return [
    {
      id: 'page-contact',
      formSchemaId: MOCK_FORM_SCHEMA_ID,
      title: 'Contact & Division',
      displayOrder: 0,
      audienceSet: ['APPLICANT', 'DIRECTOR'],
      phase: 'PHASE_1',
      requiresDirectorApproval: false,
    },
    {
      id: 'page-about',
      formSchemaId: MOCK_FORM_SCHEMA_ID,
      title: 'About You',
      displayOrder: 1,
      audienceSet: ['APPLICANT', 'DIRECTOR', 'JUDGE'],
      phase: 'PHASE_1',
      requiresDirectorApproval: false,
    },
    {
      id: 'page-titles',
      formSchemaId: MOCK_FORM_SCHEMA_ID,
      title: 'Titles & Documents',
      displayOrder: 2,
      audienceSet: ['APPLICANT', 'DIRECTOR'],
      phase: 'PHASE_1',
      requiresDirectorApproval: true,
    },
    // Costume page (application-surface-reconciliation Req 7.1/7.2/7.3): the
    // legacy Costume_Approval surface folded into the Builder_Page_Set as a
    // configuration-driven FormPage. It carries a document-slot collection
    // (the costume photo/spec uploads) and requires per-page director approval
    // so its PageApproval becomes the SSOT_Of_Record for Costume_Approval.
    {
      id: 'page-costume',
      formSchemaId: MOCK_FORM_SCHEMA_ID,
      title: 'Costume',
      displayOrder: 3,
      audienceSet: ['APPLICANT', 'DIRECTOR'],
      phase: 'PHASE_1',
      requiresDirectorApproval: true,
    },
    {
      id: 'page-interview',
      formSchemaId: MOCK_FORM_SCHEMA_ID,
      title: 'Interview (Phase 2)',
      displayOrder: 4,
      audienceSet: ['APPLICANT', 'DIRECTOR', 'JUDGE'],
      phase: 'PHASE_2',
      requiresDirectorApproval: false,
    },
  ];
}

function seedFields(): FormField[] {
  const base = {
    formSchemaId: MOCK_FORM_SCHEMA_ID,
    immutableOnSubmit: false,
    thirdPersonRequired: false,
    applicantTypeVisibility: 'BOTH' as const,
  };
  return [
    // page-contact: bound contact identity (locked) + bound division
    {
      ...base,
      id: 'field-contact-identity',
      formPageId: 'page-contact',
      label: 'Your contact details',
      fieldCategory: 'BOUND',
      bindingTarget: 'CONTACT_IDENTITY',
      required: false,
      displayOrder: 0,
      audienceSet: ['APPLICANT', 'DIRECTOR'],
    },
    {
      ...base,
      id: 'field-division',
      formPageId: 'page-contact',
      label: 'Division',
      fieldCategory: 'BOUND',
      bindingTarget: 'DIVISION',
      required: true,
      displayOrder: 1,
      audienceSet: ['APPLICANT', 'DIRECTOR'],
    },
    // page-about: custom fields
    {
      ...base,
      id: 'field-fullname-display',
      formPageId: 'page-about',
      label: 'Preferred name',
      fieldCategory: 'CUSTOM',
      fieldType: 'TEXT',
      required: true,
      displayOrder: 0,
      audienceSet: ['APPLICANT', 'DIRECTOR', 'JUDGE'],
    },
    {
      ...base,
      id: 'field-bio',
      formPageId: 'page-about',
      label: 'Short biography',
      fieldCategory: 'CUSTOM',
      fieldType: 'LONG_TEXT',
      required: true,
      wordLimit: 200,
      displayOrder: 1,
      audienceSet: ['APPLICANT', 'DIRECTOR', 'JUDGE'],
    },
    {
      ...base,
      id: 'field-talent',
      formPageId: 'page-about',
      label: 'Talent category',
      fieldCategory: 'CUSTOM',
      fieldType: 'SELECT',
      required: false,
      options: ['Vocal', 'Dance', 'Instrumental', 'Other'],
      displayOrder: 2,
      audienceSet: ['APPLICANT', 'DIRECTOR', 'JUDGE'],
    },
    // page-titles: bound title preferences + bound document slot
    {
      ...base,
      id: 'field-title-prefs',
      formPageId: 'page-titles',
      label: 'Title preferences',
      fieldCategory: 'BOUND',
      bindingTarget: 'TITLE_PREFERENCES',
      required: false,
      displayOrder: 0,
      audienceSet: ['APPLICANT', 'DIRECTOR'],
    },
    {
      ...base,
      id: 'field-documents',
      formPageId: 'page-titles',
      label: 'Required documents',
      fieldCategory: 'BOUND',
      bindingTarget: 'DOCUMENT_SLOT',
      required: false,
      displayOrder: 1,
      audienceSet: ['APPLICANT', 'DIRECTOR'],
    },
    // page-costume: bound document-slot collection for the costume uploads
    // (Req 7.1 — the costume FormPage's document-slot collection).
    {
      ...base,
      id: 'field-costume-documents',
      formPageId: 'page-costume',
      label: 'Costume photos & specifications',
      fieldCategory: 'BOUND',
      bindingTarget: 'DOCUMENT_SLOT',
      required: false,
      displayOrder: 0,
      audienceSet: ['APPLICANT', 'DIRECTOR'],
    },
    // page-interview (Phase 2): custom field withheld until APPROVED
    {
      ...base,
      id: 'field-interview-q1',
      formPageId: 'page-interview',
      label: 'Why do you want this title?',
      fieldCategory: 'CUSTOM',
      fieldType: 'LONG_TEXT',
      required: true,
      characterLimit: 1000,
      displayOrder: 0,
      audienceSet: ['APPLICANT', 'DIRECTOR', 'JUDGE'],
    },
  ];
}

function seedCatalog(): DocumentCatalogEntry[] {
  return [
    {
      competitionId: MOCK_COMPETITION_ID,
      id: 'doc-headshot',
      label: 'Headshot photo',
      requiredForSubmission: true,
      allowedContentTypes: ['image/jpeg', 'image/png'],
      maxFileSizeBytes: 5_242_880,
    },
    {
      competitionId: MOCK_COMPETITION_ID,
      id: 'doc-resume',
      label: 'Résumé',
      requiredForSubmission: false,
      allowedContentTypes: ['application/pdf'],
      maxFileSizeBytes: 5_242_880,
      maxFileCount: 1,
    },
    {
      competitionId: MOCK_COMPETITION_ID,
      id: 'doc-costume',
      label: 'Costume photo',
      requiredForSubmission: false,
    },
  ];
}

// ===========================================================================
// The mock client factory
// ===========================================================================

/**
 * Build a fresh in-memory `ApplicationFormClient` from the seeds. Exported so
 * tests can spin up an isolated client per case without cross-test bleed.
 */
export function createMockApplicationFormClient(): ApplicationFormClient {
  const store: MockStore = {
    competitionId: MOCK_COMPETITION_ID,
    competitionYear: MOCK_COMPETITION_YEAR,
    divisions: seedDivisions(),
    titles: seedTitles(),
    form: seedForm(),
    pages: seedPages(),
    fields: seedFields(),
    documentCatalog: seedCatalog(),
    versions: [],
    responses: new Map(),
    persons: new Map(),
    participations: new Map(),
    documents: new Map(),
    pageApprovals: new Map(),
    approvalDetailVisibility: new Map(),
  };

  // Seed the mock applicant's Person (contact intake already complete) so the
  // Contact_Identity bound field resolves and onboarding routes past intake.
  store.persons.set(MOCK_APPLICANT_PERSON_ID, {
    legalFirstName: 'Avery',
    legalLastName: 'Mock',
    primaryEmail: 'avery.mock@example.com',
    mobilePhone: '208-555-0100',
    dateOfBirth: '2002-04-17',
    address: { street: '100 Capitol Blvd', city: 'Boise', state: 'ID', zip: '83702' },
  });
  store.participations.set(responseKey(MOCK_APPLICANT_PERSON_ID, MOCK_COMPETITION_ID), {
    divisionKey: 'miss',
    titlePreferences: [],
  });

  // A second applicant with an approved title, so title-contention warnings and
  // the director review list have something to surface.
  store.participations.set(responseKey(MOCK_OTHER_APPLICANT_PERSON_ID, MOCK_COMPETITION_ID), {
    divisionKey: 'miss',
    approvedTitle: 'Miss Gem State',
    titlePreferences: ['Miss Gem State'],
  });
  store.persons.set(MOCK_OTHER_APPLICANT_PERSON_ID, {
    legalFirstName: 'Jordan',
    legalLastName: 'Rivers',
    primaryEmail: 'jordan.rivers@example.com',
    dateOfBirth: '2001-09-02',
    address: { street: '55 Front St', city: 'Boise', state: 'ID', zip: '83702' },
  });

  // Seed a judge-visible Approval_Details config for the approval-required page
  // ("Titles & Documents", requiresDirectorApproval), so the configurator and
  // the judge projection have a non-empty configuration to surface (Req 22.7).
  store.approvalDetailVisibility.set(approvalVisibilityKey(MOCK_COMPETITION_ID, 'page-titles'), {
    competitionId: MOCK_COMPETITION_ID,
    formPageId: 'page-titles',
    judgeVisibleKeys: ['Interview readiness', 'Wardrobe approved'],
  });

  // -------------------------------------------------------------------------
  // Snapshot helpers — map the transport store rows into the pure-module row
  // shapes and capture an immutable version via the shared `snapshotForm`.
  // -------------------------------------------------------------------------

  /** Map transport `FormPage` rows into pure `PageRow`s for `snapshotForm`. */
  function toPageRows(pages: FormPage[]): PageRow[] {
    return pages.map((p) => ({
      id: p.id,
      formSchemaId: p.formSchemaId,
      competitionId: store.competitionId,
      title: p.title,
      helpText: p.helpText ?? undefined,
      displayOrder: p.displayOrder,
      visibleDivisionKeys: p.visibleDivisionKeys ?? undefined,
      audienceSet: p.audienceSet,
      phase: p.phase,
      requiresDirectorApproval: p.requiresDirectorApproval,
    }));
  }

  /**
   * Map transport `FormField` rows into pure `FormFieldRow`s. The single nominal
   * gap is `fieldType` (transport string-union → backend `FieldType` enum); it
   * carries identical string values, so the localized cast is a runtime no-op.
   */
  function toFieldRows(fields: FormField[]): FormFieldRow[] {
    return fields.map((f) => ({
      id: f.id,
      formPageId: f.formPageId ?? undefined,
      label: f.label,
      fieldCategory: f.fieldCategory ?? 'CUSTOM',
      bindingTarget: f.bindingTarget ?? undefined,
      fieldType: f.fieldType as unknown as FormFieldRow['fieldType'],
      required: f.required,
      wordLimit: f.wordLimit ?? undefined,
      characterLimit: f.characterLimit ?? undefined,
      options: f.options ?? undefined,
      helpText: f.helpText ?? undefined,
      allowedContentTypes: f.allowedContentTypes ?? undefined,
      maxFileSizeBytes: f.maxFileSizeBytes ?? undefined,
      maxFileCount: f.maxFileCount ?? undefined,
      displayOrder: f.displayOrder,
      visibleDivisionKeys: f.visibleDivisionKeys ?? undefined,
      audienceSet: f.audienceSet ?? undefined,
    }));
  }

  /** Capture the current structure as a pure `FormVersionSnapshot` (Req 8.1). */
  function buildSnapshot(versionNumber: number): PureSnapshot {
    return snapshotForm(toPageRows(store.pages), toFieldRows(store.fields), {
      formSchemaId: MOCK_FORM_SCHEMA_ID,
      competitionId: store.competitionId,
      versionNumber,
    });
  }

  /** The active (latest published) version, or undefined before the first publish. */
  function activeVersion(): FormVersion | undefined {
    return store.versions[store.versions.length - 1];
  }

  /** Read a version's snapshot back as a pure snapshot for the pure functions. */
  function pureSnapshotOf(version: FormVersion): PureSnapshot {
    return version.snapshot as unknown as PureSnapshot;
  }

  // Publish the initial version so reads always have an active snapshot.
  {
    const snapshot = buildSnapshot(1);
    store.versions.push({
      id: `${MOCK_FORM_SCHEMA_ID}#v1`,
      formSchemaId: MOCK_FORM_SCHEMA_ID,
      versionNumber: 1,
      competitionId: store.competitionId,
      snapshot: snapshot as unknown as FormVersionSnapshot,
      createdAt: nowIso(),
    });
  }

  // -------------------------------------------------------------------------
  // Canonical-source builders (Bound_Fields resolve ONLY from these).
  // -------------------------------------------------------------------------

  function participationFor(applicantPersonId: string): ParticipationRec {
    const key = responseKey(applicantPersonId, store.competitionId);
    let rec = store.participations.get(key);
    if (!rec) {
      rec = { titlePreferences: [] };
      store.participations.set(key, rec);
    }
    return rec;
  }

  function uploadsFor(applicantPersonId: string): UploadedDoc[] {
    return store.documents.get(responseKey(applicantPersonId, store.competitionId)) ?? [];
  }

  /** Project a stored UploadedDoc into the transport DocumentRef DTO (Req 7.1). */
  function toDocumentRef(applicantPersonId: string, doc: UploadedDoc): DocumentRef {
    return {
      id: doc.id,
      applicantId: applicantPersonId,
      competitionYear: doc.competitionYear,
      documentType: doc.documentType,
      s3Key: doc.s3Key,
      fileName: doc.fileName,
      contentType: doc.contentType,
      uploadedAt: doc.uploadedAt,
      catalogEntryId: doc.catalogEntryId,
    };
  }

  /** A file extension from a content type, defaulting to `bin` (mirrors the presign Lambda). */
  function extensionForContentType(contentType: string): string {
    const map: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'application/pdf': 'pdf',
    };
    return map[contentType.toLowerCase()] ?? 'bin';
  }

  /** The Bound_Field canonical read-model used for review resolution (Req 9.1, 18.3). */
  function boundSourcesFor(applicantPersonId: string): BoundCanonicalSources {
    const person = store.persons.get(applicantPersonId);
    const participation = participationFor(applicantPersonId);
    return {
      person: person
        ? {
            legalFirstName: person.legalFirstName,
            legalLastName: person.legalLastName,
            primaryEmail: person.primaryEmail,
            mobilePhone: person.mobilePhone,
            dateOfBirth: person.dateOfBirth,
            address: person.address,
          }
        : undefined,
      participation: {
        divisionKey: participation.divisionKey,
        approvedTitle: participation.approvedTitle,
      },
      titlePreferences: participation.titlePreferences,
      documents: uploadsFor(applicantPersonId).map((d) => ({
        s3Key: d.s3Key,
        fileName: d.fileName,
        contentType: d.contentType,
        catalogEntryId: d.catalogEntryId,
      })),
    };
  }

  /** The judge-projection canonical read-model (PII gated by the caller, Req 22.8). */
  function judgeSourcesFor(applicantPersonId: string): JudgeCanonicalSources {
    const person = store.persons.get(applicantPersonId);
    const participation = participationFor(applicantPersonId);
    return {
      person: person
        ? {
            legalFirstName: person.legalFirstName,
            legalLastName: person.legalLastName,
            primaryEmail: person.primaryEmail,
            mobilePhone: person.mobilePhone,
            dateOfBirth: person.dateOfBirth,
            address: person.address,
          }
        : undefined,
      participation: {
        divisionKey: participation.divisionKey,
        approvedTitle: participation.approvedTitle,
      },
      titlePreferences: participation.titlePreferences,
      documents: uploadsFor(applicantPersonId).map((d) => ({
        catalogEntryId: d.catalogEntryId,
        fileName: d.fileName,
      })),
    };
  }

  /** Map the transport document-catalog rows into the pure-module shape. */
  function toPureCatalog(): PureCatalogEntry[] {
    return store.documentCatalog.map((e) => ({
      id: e.id,
      label: e.label,
      requiredForSubmission: e.requiredForSubmission,
      visibleDivisionKeys: e.visibleDivisionKeys ?? undefined,
      applicantTypeScope: (e.applicantTypeScope ?? undefined) as PureCatalogEntry['applicantTypeScope'],
    }));
  }

  // -------------------------------------------------------------------------
  // Display formatting (review).
  // -------------------------------------------------------------------------

  function formatValueForDisplay(value: ResponseValue | undefined): string {
    if (!value) return '';
    switch (value.kind) {
      case 'text':
      case 'date':
        return value.value;
      case 'number':
        return String(value.value);
      case 'boolean':
        return value.value ? 'Yes' : 'No';
      case 'list':
        return value.value.join(', ');
      case 'files':
        return value.value.map((f) => f.fileName).join(', ');
    }
  }

  /** Format a resolved Bound_Field for the director review view. */
  function formatBoundForReview(applicantPersonId: string, fieldId: string): string {
    const snapshot = activeVersion() ? pureSnapshotOf(activeVersion()!) : buildSnapshot(1);
    for (const page of snapshot.pages) {
      for (const field of page.fields) {
        if (field.id !== fieldId || field.fieldCategory !== 'BOUND') continue;
        const resolved = resolveBoundField(field, boundSourcesFor(applicantPersonId));
        switch (resolved.target) {
          case 'DIVISION':
            return resolved.divisionKey ?? '';
          case 'TITLE_PREFERENCES': {
            const parts = [...resolved.ranked];
            if (resolved.approvedTitle) parts.push(`(approved: ${resolved.approvedTitle})`);
            return parts.join(', ');
          }
          case 'CONTACT_IDENTITY': {
            const c = resolved.contact;
            if (!c) return '';
            return [`${c.legalFirstName} ${c.legalLastName}`.trim(), c.primaryEmail, c.mobilePhone]
              .filter((p): p is string => typeof p === 'string' && p.length > 0)
              .join(' · ');
          }
          case 'DOCUMENT_SLOT':
            return resolved.documents.map((d) => d.fileName).join(', ');
        }
      }
    }
    return '';
  }

  // -------------------------------------------------------------------------
  // Response helpers.
  // -------------------------------------------------------------------------

  /**
   * Attach the season-phase-deadlines derived lock/phase fields to a response,
   * mirroring the server's `evaluatePhaseState` (season-phase-deadlines Req 16,
   * 17). The mock Season carries no configured deadlines, so the date gate is
   * open on both sides and `effectiveLock` is governed purely by the echoed
   * Manual_Lock_Override (`LOCKED` ⇒ locked, `UNLOCKED`/absent ⇒ unlocked). The
   * same pure decision the Application_Form_Service runs is used here so the mock
   * can never disagree with the live derivation.
   */
  function withLockFields(resp: ApplicationResponse): ApplicationResponse {
    const state = evaluatePhaseState(
      { phaseOneCloseAt: null, phaseTwoDueDate: null },
      resp.submissionStatus as unknown as PureSubmissionStatus,
      resp.manualLockOverride ?? null,
      new Date(),
    );
    return {
      ...resp,
      effectiveLock: state.effectiveLock,
      isPhase1Open: state.isPhase1Open,
      isPhase2Unlocked: state.isPhase2Unlocked,
    };
  }

  function getResponse(applicantPersonId: string): ApplicationResponse | undefined {
    const resp = store.responses.get(responseKey(applicantPersonId, store.competitionId));
    if (!resp) return undefined;
    // Mirror the live getMyApplicationResponse enrichment: attach the caller's
    // division from their Participation so the renderer can scope visibility.
    const participation = store.participations.get(
      responseKey(applicantPersonId, store.competitionId),
    );
    // Strip the director-only internal note before the item leaves the owner
    // read path — the SAME pure boundary the server applies on
    // getMyApplicationResponse, so no contestant-facing shape ever carries it
    // (application-decision-feedback R5.1–5.3). The contestant-visible
    // returnFeedbackNote is preserved.
    const enriched = toOwnerReadModel({ ...resp });
    if (participation?.divisionKey) enriched.divisionKey = participation.divisionKey;
    if (participation?.approvedTitle) enriched.approvedTitle = participation.approvedTitle;
    // Surface the server-derived lock/phase mirror fields (Req 16, 17).
    return withLockFields(enriched);
  }

  function upsertResponseRecord(
    applicantPersonId: string,
    formVersionId: string,
    values: ResponseValues,
    titlePreferences: string[] | null | undefined,
    status: ApplicationResponse['submissionStatus'],
  ): StoredApplicationResponse {
    const key = responseKey(applicantPersonId, store.competitionId);
    const existing = store.responses.get(key);
    const next: StoredApplicationResponse = {
      applicantPersonId,
      competitionId: store.competitionId,
      formSchemaId: MOCK_FORM_SCHEMA_ID,
      formVersionId,
      submissionStatus: status,
      values: clone(values),
      titlePreferences: titlePreferences ?? existing?.titlePreferences ?? null,
      // Preserve any Director-set manual lock override across draft/submit
      // writes (season-phase-deadlines Req 16) — it is owned by the lock ops.
      manualLockOverride: existing?.manualLockOverride ?? null,
      submittedAt:
        status === 'SUBMITTED' ? nowIso() : (existing?.submittedAt ?? null),
      createdAt: existing?.createdAt ?? nowIso(),
      updatedAt: nowIso(),
      // A resubmission (DRAFT→SUBMITTED) clears the return-feedback note — the
      // return has been addressed — mirroring the marker's lifecycle and the
      // server's persistResponse branch. A plain draft save while changes are
      // pending preserves it (the applicant is still addressing the return).
      // The director-only internal note is carried through untouched; it is
      // only ever set/cleared by a decision (application-decision-feedback R2.6).
      returnFeedbackNote:
        status === 'SUBMITTED' ? undefined : (existing?.returnFeedbackNote ?? null),
      internalRejectNote: existing?.internalRejectNote,
    };
    store.responses.set(key, next);
    return next;
  }

  // ==========================================================================
  // The ApplicationFormClient implementation.
  // ==========================================================================

  return {
    // --- Onboarding + contact intake (Req 14, 15) --------------------------
    getOnboardingDestination() {
      const person = store.persons.get(MOCK_APPLICANT_PERSON_ID);
      const state: RoutingState = {
        hasCompletedContactIntake: Boolean(person?.dateOfBirth && person?.address),
      };
      // The same total routing function the server runs (Req 15.1–15.5).
      return delay(resolveDestination(state) as unknown as Destination);
    },

    submitContactIntake(input: ContactIntakeInput) {
      // Create-or-complete the global Person SSOT in one operation (Req 14.4).
      // The mock keys on the fixed mock applicant rather than a Cognito sub; the
      // live client routes through PersonService keyed by personIdForCognitoSub.
      const existing = store.persons.get(MOCK_APPLICANT_PERSON_ID);
      store.persons.set(MOCK_APPLICANT_PERSON_ID, {
        legalFirstName: input.legalFirstName,
        legalLastName: input.legalLastName,
        primaryEmail: input.primaryEmail,
        mobilePhone: input.mobilePhone ?? existing?.mobilePhone,
        dateOfBirth: input.dateOfBirth,
        address: input.address,
      });
      return delay({ personId: MOCK_APPLICANT_PERSON_ID });
    },

    // --- Builder (Req 1, 2, 3, 8, 20) --------------------------------------
    getApplicationForm(competitionId: string) {
      const form: ApplicationForm = {
        ...store.form,
        competitionId,
        fields: clone(store.fields),
      };
      return delay(form);
    },

    listFormPages(formSchemaId: string) {
      // The working/draft page list for a schema, ordered by displayOrder —
      // mirrors the live `listFormPages` read (Req 1.5). Fields stay flat on the
      // form (the builder filters `form.fields` by `formPageId`), matching the
      // existing mock convention.
      const pages = store.pages
        .filter((p) => p.formSchemaId === formSchemaId)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      return delay(pages.map((p) => ({ ...p })));
    },

    createFormPage(input: CreateFormPageInput) {
      const titleError = validatePageTitle(input.title);
      if (titleError) throw new Error(titleError.message);
      const siblings = store.pages.filter((p) => p.formSchemaId === input.formSchemaId);
      // Rich_Help_Text triple: an HTML surface keeps its sanitized html + the
      // HTML indicator; anything else degrades to PLAIN with no html
      // (application-builder-rich-help-text Req 2, 3.4).
      const createIsHtml = input.helpTextFormat === 'HTML' && !!input.helpTextHtml;
      const page: FormPage = {
        id: newId('page'),
        formSchemaId: input.formSchemaId,
        title: input.title,
        helpText: input.helpText ?? null,
        helpTextFormat: createIsHtml ? 'HTML' : 'PLAIN',
        helpTextHtml: createIsHtml ? input.helpTextHtml ?? null : null,
        autoCollapseHelp: input.autoCollapseHelp ?? false,
        displayOrder: nextDisplayOrder(siblings),
        visibleDivisionKeys: input.visibleDivisionKeys ?? null,
        audienceSet: input.audienceSet ?? ['APPLICANT', 'DIRECTOR'],
        phase: input.phase ?? 'PHASE_1',
        requiresDirectorApproval: input.requiresDirectorApproval ?? false,
      };
      store.pages.push(page);
      return delay(page);
    },

    updateFormPage(input: UpdateFormPageInput) {
      const page = store.pages.find((p) => p.id === input.id);
      if (!page) throw new Error(`Form page not found: ${input.id}`);
      if (input.title !== undefined && input.title !== null) {
        const titleError = validatePageTitle(input.title);
        if (titleError) throw new Error(titleError.message);
        page.title = input.title;
      }
      if (input.helpText !== undefined) page.helpText = input.helpText;
      // Rich_Help_Text triple: an HTML surface keeps its sanitized html + the
      // HTML indicator; anything else degrades to PLAIN with no html (Req 2, 3.4).
      if (input.helpTextFormat !== undefined || input.helpTextHtml !== undefined) {
        const updateIsHtml = input.helpTextFormat === 'HTML' && !!input.helpTextHtml;
        page.helpTextFormat = updateIsHtml ? 'HTML' : 'PLAIN';
        page.helpTextHtml = updateIsHtml ? input.helpTextHtml ?? null : null;
      }
      if (input.autoCollapseHelp !== undefined && input.autoCollapseHelp !== null)
        page.autoCollapseHelp = input.autoCollapseHelp;
      if (input.visibleDivisionKeys !== undefined)
        page.visibleDivisionKeys = input.visibleDivisionKeys;
      if (input.audienceSet !== undefined && input.audienceSet !== null)
        page.audienceSet = input.audienceSet;
      if (input.phase !== undefined && input.phase !== null) page.phase = input.phase;
      if (input.requiresDirectorApproval !== undefined && input.requiresDirectorApproval !== null)
        page.requiresDirectorApproval = input.requiresDirectorApproval;
      return delay(page);
    },

    reorderFormPages(formSchemaId: string, orderedIds: string[]) {
      const pages = store.pages.filter((p) => p.formSchemaId === formSchemaId);
      const order = normalizeDisplayOrder(orderedIds, pages);
      for (const page of pages) {
        page.displayOrder = order.get(page.id) ?? page.displayOrder;
      }
      const sorted = [...pages].sort((a, b) => a.displayOrder - b.displayOrder);
      return delay(sorted);
    },

    deleteFormPage(id: string) {
      store.pages = store.pages.filter((p) => p.id !== id);
      // Cascade: drop the page's fields (the mock has no stored responses
      // referencing them at seed time; a richer guard lives server-side).
      store.fields = store.fields.filter((f) => f.formPageId !== id);
      return delay(id);
    },

    createFormField(input: CreateFormFieldInput) {
      const labelError = validateFieldLabel(input.label);
      if (labelError) throw new Error(labelError.message);
      const category = input.fieldCategory ?? 'CUSTOM';
      if (category === 'CUSTOM' && input.fieldType) {
        const typeError = validateFieldType(input.fieldType);
        if (typeError) throw new Error(typeError.message);
      }
      const field: FormField = {
        id: input.id && input.id.length > 0 ? input.id : newId('field'),
        formSchemaId: input.formSchemaId,
        formPageId: input.formPageId ?? null,
        label: input.label,
        fieldCategory: category,
        bindingTarget: input.bindingTarget ?? null,
        fieldType: input.fieldType ?? null,
        required: input.required,
        wordLimit: input.wordLimit ?? null,
        characterLimit: input.characterLimit ?? null,
        options: input.options ?? null,
        helpText: input.helpText ?? null,
        allowedContentTypes: input.allowedContentTypes ?? null,
        maxFileSizeBytes: input.maxFileSizeBytes ?? null,
        maxFileCount: input.maxFileCount ?? null,
        displayOrder: input.displayOrder,
        immutableOnSubmit: input.immutableOnSubmit ?? false,
        thirdPersonRequired: input.thirdPersonRequired ?? false,
        applicantTypeVisibility: input.applicantTypeVisibility ?? 'BOTH',
        visibleDivisionKeys: input.visibleDivisionKeys ?? null,
        audienceSet: input.audienceSet ?? null,
      };
      store.fields.push(field);
      return delay(field);
    },

    updateFormField(input: UpdateFormFieldInput) {
      const field = store.fields.find((f) => f.id === input.id);
      if (!field) throw new Error(`Form field not found: ${input.id}`);
      if (input.label !== undefined && input.label !== null) {
        const labelError = validateFieldLabel(input.label);
        if (labelError) throw new Error(labelError.message);
        field.label = input.label;
      }
      if (input.fieldType !== undefined && input.fieldType !== null) {
        const typeError = validateFieldType(input.fieldType);
        if (typeError) throw new Error(typeError.message);
        field.fieldType = input.fieldType;
      }
      if (input.formPageId !== undefined) field.formPageId = input.formPageId;
      if (input.fieldCategory !== undefined && input.fieldCategory !== null)
        field.fieldCategory = input.fieldCategory;
      if (input.bindingTarget !== undefined) field.bindingTarget = input.bindingTarget;
      if (input.required !== undefined && input.required !== null) field.required = input.required;
      if (input.wordLimit !== undefined) field.wordLimit = input.wordLimit;
      if (input.characterLimit !== undefined) field.characterLimit = input.characterLimit;
      if (input.options !== undefined) field.options = input.options;
      if (input.helpText !== undefined) field.helpText = input.helpText;
      if (input.allowedContentTypes !== undefined)
        field.allowedContentTypes = input.allowedContentTypes;
      if (input.maxFileSizeBytes !== undefined) field.maxFileSizeBytes = input.maxFileSizeBytes;
      if (input.maxFileCount !== undefined) field.maxFileCount = input.maxFileCount;
      if (input.displayOrder !== undefined && input.displayOrder !== null)
        field.displayOrder = input.displayOrder;
      if (input.immutableOnSubmit !== undefined && input.immutableOnSubmit !== null)
        field.immutableOnSubmit = input.immutableOnSubmit;
      if (input.thirdPersonRequired !== undefined && input.thirdPersonRequired !== null)
        field.thirdPersonRequired = input.thirdPersonRequired;
      if (input.applicantTypeVisibility !== undefined && input.applicantTypeVisibility !== null)
        field.applicantTypeVisibility = input.applicantTypeVisibility;
      if (input.visibleDivisionKeys !== undefined)
        field.visibleDivisionKeys = input.visibleDivisionKeys;
      if (input.audienceSet !== undefined) field.audienceSet = input.audienceSet;
      return delay(field);
    },

    reorderFormFields(formPageId: string, orderedIds: string[]) {
      const fields = store.fields.filter((f) => f.formPageId === formPageId);
      const order = normalizeDisplayOrder(orderedIds, fields);
      for (const field of fields) {
        field.displayOrder = order.get(field.id) ?? field.displayOrder;
      }
      const sorted = [...fields].sort((a, b) => a.displayOrder - b.displayOrder);
      return delay(sorted);
    },

    deleteFormField(id: string) {
      store.fields = store.fields.filter((f) => f.id !== id);
      return delay(id);
    },

    publishFormVersion(formSchemaId: string) {
      const prev = activeVersion();
      const nextNumber = (prev?.versionNumber ?? 0) + 1;
      const nextSnapshot = buildSnapshot(nextNumber);

      // While a SUBMITTED response exists, destructive edits to a custom field
      // with stored values are rejected by the shared immutability guard (Req 8.4).
      const submitted = [...store.responses.values()].filter(
        (r) => r.submissionStatus === 'SUBMITTED',
      );
      if (prev && submitted.length > 0) {
        const fieldIdsWithResponses = new Set<string>();
        for (const r of submitted) {
          for (const fieldId of Object.keys(r.values)) fieldIdsWithResponses.add(fieldId);
        }
        const guard = immutabilityGuard(
          pureSnapshotOf(prev),
          nextSnapshot,
          fieldIdsWithResponses,
          true,
        );
        if (!guard.allowed) {
          throw new Error(
            `Cannot publish: ${guard.violations.map((v) => v.reason).join(' ')}`,
          );
        }
      }

      const version: FormVersion = {
        id: `${formSchemaId}#v${nextNumber}`,
        formSchemaId,
        versionNumber: nextNumber,
        competitionId: store.competitionId,
        snapshot: nextSnapshot as unknown as FormVersionSnapshot,
        createdAt: nowIso(),
      };
      store.versions.push(version);
      store.form.currentVersionNumber = nextNumber;
      return delay(version);
    },

    // --- Document catalog (Req 20) -----------------------------------------
    getDocumentCatalog(competitionId: string) {
      return delay(store.documentCatalog.filter((e) => e.competitionId === competitionId));
    },

    upsertDocumentCatalogEntry(input: DocumentCatalogEntryInput) {
      const entry: DocumentCatalogEntry = {
        competitionId: input.competitionId,
        id: input.id ?? newId('doc'),
        label: input.label,
        requiredForSubmission: input.requiredForSubmission,
        visibleDivisionKeys: input.visibleDivisionKeys ?? null,
        applicantTypeScope: input.applicantTypeScope ?? null,
        allowedContentTypes: input.allowedContentTypes ?? null,
        maxFileSizeBytes: input.maxFileSizeBytes ?? null,
        maxFileCount: input.maxFileCount ?? null,
        documentType: input.documentType ?? null,
      };
      const idx = store.documentCatalog.findIndex(
        (e) => e.competitionId === entry.competitionId && e.id === entry.id,
      );
      if (idx >= 0) store.documentCatalog[idx] = entry;
      else store.documentCatalog.push(entry);
      return delay(entry);
    },

    deleteDocumentCatalogEntry(competitionId: string, id: string) {
      store.documentCatalog = store.documentCatalog.filter(
        (e) => !(e.competitionId === competitionId && e.id === id),
      );
      return delay(id);
    },

    reorderDocumentCatalogEntries(competitionId: string, orderedIds: string[]) {
      const entries = store.documentCatalog.filter((e) => e.competitionId === competitionId);
      const order = normalizeDisplayOrder(orderedIds, entries);
      for (const entry of entries) {
        entry.displayOrder = order.get(entry.id) ?? entry.displayOrder ?? 0;
      }
      const sorted = [...entries].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
      );
      return delay(sorted);
    },

    // --- Document uploads (apply-document-uploads Req 2, 6, 7, 10) ----------
    // Owner-scoped to the fixed mock applicant: the mock never accepts an
    // applicantId argument, mirroring the live client's identity derivation
    // (Req 10.2, 10.3). Uploads are stored independently of the draft/submitted
    // state of the response (Req 8.2) and feed the submit gate's document check.
    listMyUploadedDocuments(competitionYear: number) {
      const docs = uploadsFor(MOCK_APPLICANT_PERSON_ID)
        .filter((d) => d.competitionYear === competitionYear)
        .map((d) => toDocumentRef(MOCK_APPLICANT_PERSON_ID, d));
      return delay(docs);
    },

    getDocumentUploadUrl(input: GetDocumentUploadUrlInput): Promise<PresignedUpload> {
      // Mirror the presign Lambda's slot-scoped, collision-free key for a
      // catalog-linked upload (Decision D3): one addressable object per file so a
      // slot can hold multiple uploads. The mock URL is inert — no real S3 PUT
      // happens in mock mode; the hook treats a 2xx-shaped result as success.
      const extension = extensionForContentType(input.contentType);
      const s3Key =
        `applicants/${MOCK_APPLICANT_PERSON_ID}/${input.competitionYear}` +
        `/catalog/${input.catalogEntryId}/${newId('file')}.${extension}`;
      const presigned: PresignedUpload = {
        uploadUrl: `https://mock-uploads.local/${s3Key}?mock-presigned`,
        s3Key,
        expiresIn: 900,
      };
      return delay(presigned);
    },

    createDocumentReference(input: CreateDocumentReferenceInput) {
      const key = responseKey(MOCK_APPLICANT_PERSON_ID, store.competitionId);
      const list = store.documents.get(key) ?? [];
      const doc: UploadedDoc = {
        id: newId('docref'),
        catalogEntryId: input.catalogEntryId,
        documentType: input.documentType,
        competitionYear: input.competitionYear,
        fileName: input.fileName,
        s3Key: input.s3Key,
        contentType: input.contentType,
        uploadedAt: nowIso(),
      };
      list.push(doc);
      store.documents.set(key, list);
      return delay(toDocumentRef(MOCK_APPLICANT_PERSON_ID, doc));
    },

    deleteDocumentReference(id: string) {
      const key = responseKey(MOCK_APPLICANT_PERSON_ID, store.competitionId);
      const list = store.documents.get(key) ?? [];
      store.documents.set(
        key,
        list.filter((d) => d.id !== id),
      );
      return delay(id);
    },

    // --- Renderer (Req 5, 6, 7) --------------------------------------------
    getActiveFormVersion(_competitionId: string, _divisionKey: string) {
      void _competitionId;
      void _divisionKey;
      const version = activeVersion();
      if (!version) throw new Error('No published Form_Version for this competition.');
      return delay(version);
    },

    getMyApplicationResponse(_competitionId: string) {
      void _competitionId;
      return delay(getResponse(MOCK_APPLICANT_PERSON_ID) ?? null);
    },

    getMyPageApprovals(competitionId: string) {
      // Self-scoped to the mock "current applicant" (the live client resolves the
      // caller's personId server-side; the mock keys on MOCK_APPLICANT_PERSON_ID),
      // returning only that applicant's page approvals for the competition (Req 21).
      const mine: PageApproval[] = [];
      for (const approval of store.pageApprovals.values()) {
        if (approval.applicantPersonId !== MOCK_APPLICANT_PERSON_ID) continue;
        if (approval.competitionId !== competitionId) continue;
        mine.push(approval);
      }
      return delay(mine);
    },

    saveDraft(input: SaveDraftInput) {
      const version = activeVersion();
      if (!version) throw new Error('No published Form_Version to save against.');
      const divisionKey = participationFor(MOCK_APPLICANT_PERSON_ID).divisionKey ?? '';
      // Draft uses relaxed validation (required not enforced, Req 6.1) but still
      // rejects malformed present values + stored bound values via the shared rule.
      const errors = validateResponse(pureSnapshotOf(version), input.values, divisionKey, {
        enforceRequired: false,
      });
      if (errors.length > 0) {
        throw new Error(`Draft rejected: ${errors.map((e) => e.message).join(' ')}`);
      }
      const saved = upsertResponseRecord(
        MOCK_APPLICANT_PERSON_ID,
        input.formVersionId,
        input.values,
        input.titlePreferences,
        'DRAFT',
      );
      return delay(saved);
    },

    submit(input: SubmitInput) {
      const version =
        store.versions.find((v) => v.id === input.formVersionId) ?? activeVersion();
      if (!version) throw new Error('No Form_Version to submit against.');
      const snapshot = pureSnapshotOf(version);
      const divisionKey = participationFor(MOCK_APPLICANT_PERSON_ID).divisionKey ?? '';

      // Strict validation against the captured version (Req 7.1–7.3) + the
      // no-stored-bound-value invariant (Req 17.2) + the document gate (Req 20.3),
      // all via the shared pure modules the server runs.
      const errors: SubmitResult['errors'] = [
        ...validateResponse(snapshot, input.values, divisionKey, { enforceRequired: true }),
        ...assertNoBoundValues(snapshot, input.values),
      ];
      const missingDocs = missingRequiredDocuments(
        toPureCatalog(),
        divisionKey,
        MOCK_APPLICANT_TYPE,
        uploadsFor(MOCK_APPLICANT_PERSON_ID).map((d) => ({ catalogEntryId: d.catalogEntryId })),
      );
      for (const doc of missingDocs) {
        errors!.push({
          fieldId: doc.id,
          code: 'MISSING_REQUIRED_DOCUMENT',
          message: `Required document "${doc.label}" is missing.`,
        });
      }

      if (errors!.length > 0) {
        return delay({ ok: false, errors });
      }
      const response = upsertResponseRecord(
        MOCK_APPLICANT_PERSON_ID,
        input.formVersionId,
        input.values,
        input.titlePreferences,
        'SUBMITTED',
      );
      return delay({ ok: true, response });
    },

    // --- Bound-field writes route to canonical writers (Req 17, 18, 19) ----
    selectDivision(_competitionId: string, divisionKey: string) {
      void _competitionId;
      // Validate against the active, application-selectable registry (Req 18.4).
      const error = validateDivisionSelection(divisionKey, store.divisions);
      if (error) throw new Error(error.message);
      participationFor(MOCK_APPLICANT_PERSON_ID).divisionKey = divisionKey;
      return delay(undefined as void);
    },

    selectSeason(_competitionId: string, competitionYear: number) {
      void _competitionId;
      // Mirrors selectDivision: routes the Season selection to
      // Participation.competitionYear, creating the Participation lazily via
      // participationFor when none yet exists for this Person and Season
      // (director-form-application-cutover Req 4.2).
      participationFor(MOCK_APPLICANT_PERSON_ID).competitionYear = competitionYear;
      return delay(undefined as void);
    },

    saveTitlePreferences(_competitionId: string, ranked: string[]) {
      void _competitionId;
      const normalized = normalizeRanked(ranked);
      participationFor(MOCK_APPLICANT_PERSON_ID).titlePreferences = normalized;
      // Non-blocking contention against OTHER applicants' approved titles (Req 19.3).
      const others: { personId: string; approvedTitle: string }[] = [];
      for (const [key, rec] of store.participations.entries()) {
        const [personId, competitionId] = key.split('#');
        if (competitionId !== store.competitionId) continue;
        if (personId === MOCK_APPLICANT_PERSON_ID) continue;
        if (rec.approvedTitle) others.push({ personId, approvedTitle: rec.approvedTitle });
      }
      const contention = titleContentionWarnings(
        { ranked: normalized },
        others,
        MOCK_APPLICANT_PERSON_ID,
      );
      return delay({ contention });
    },

    // --- Season-scoped Title catalog read (season-scoped-title-registry Req 5.1)
    listTitles(competitionYear: number) {
      // Return the season's catalog ordered by displayOrder, mirroring the live
      // `listTitles` GSI read. All catalog entries (active + inactive) are
      // returned; consumers filter to `active` for the selectable set, exactly
      // as they do for divisions.
      const titles = store.titles
        .filter((t) => t.competitionYear === competitionYear)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((t) => ({ ...t }));
      return delay(titles);
    },

    // --- Review + approvals (Req 9, 19, 21) --------------------------------
    listResponses(competitionId: string) {
      const summaries: ApplicationResponseSummary[] = [];
      for (const response of store.responses.values()) {
        if (response.competitionId !== competitionId) continue;
        const person = store.persons.get(response.applicantPersonId);
        const participation = store.participations.get(
          responseKey(response.applicantPersonId, response.competitionId),
        );
        summaries.push({
          applicantPersonId: response.applicantPersonId,
          competitionId: response.competitionId,
          formVersionId: response.formVersionId,
          applicantName: person
            ? `${person.legalFirstName} ${person.legalLastName}`.trim()
            : null,
          divisionKey: participation?.divisionKey ?? null,
          approvedTitle: participation?.approvedTitle ?? null,
          titlePreferences: response.titlePreferences ?? null,
          submissionStatus: response.submissionStatus,
          submittedAt: response.submittedAt ?? null,
          updatedAt: response.updatedAt,
        });
      }
      return delay(summaries);
    },

    // --- Read-only Approvals roll-up reads (approval-rollup-surfaces Req 2, 3) --
    // Both are READ-ONLY: they mutate nothing and introduce no write path — the
    // sole PageApproval writer stays the Discrete_Review path (Req 4.1, 4.8, 7.4).
    listResponseValues(competitionId: string) {
      // Every contestant's raw CUSTOM `values` map for the competition, plus the
      // Person-resolved display name the roll-up orders rows by (Req 2.1, 2.2,
      // 2.8). The roll-up joins these values locally against the target page's
      // current CUSTOM fields; the mock returns the values exactly as stored.
      const rows: ResponseValuesRow[] = [];
      for (const response of store.responses.values()) {
        if (response.competitionId !== competitionId) continue;
        const person = store.persons.get(response.applicantPersonId);
        rows.push({
          applicantPersonId: response.applicantPersonId,
          applicantName: person
            ? `${person.legalFirstName} ${person.legalLastName}`.trim()
            : null,
          values: response.values,
        });
      }
      return delay(rows);
    },

    listPageApprovalsForPage(competitionId: string, formPageId: string) {
      // Every contestant's PageApproval for ONE approval page (Req 3.1). The live
      // client reads the `competitionPage-applicantPersonId-index` GSI whose
      // partition is `${competitionId}#${formPageId}`; the mock stores the key
      // components separately, so matching both is the exact equivalent — records
      // for a different competition or a different page are excluded (Req 3.1, 3.2).
      const matches: PageApproval[] = [];
      for (const approval of store.pageApprovals.values()) {
        if (approval.competitionId !== competitionId) continue;
        if (approval.formPageId !== formPageId) continue;
        matches.push(approval);
      }
      return delay(matches);
    },

    getResponseForReview(competitionId: string, applicantPersonId: string) {
      const response = store.responses.get(responseKey(applicantPersonId, competitionId));
      if (!response) throw new Error(`No response for ${applicantPersonId} in ${competitionId}.`);
      const version =
        store.versions.find((v) => v.id === response.formVersionId) ?? activeVersion()!;
      // Re-render through the captured version (Req 9.1, 9.3): labels + ordering
      // come from the snapshot; custom values from the response; bound values
      // resolve from the canonical entities.
      const rendered = renderResponse(pureSnapshotOf(version), response.values);
      const sections: ApplicationSection[] = rendered.map((page) => ({
        title: page.title,
        fields: page.fields.map((field) => ({
          label: field.label,
          value:
            field.fieldCategory === 'BOUND'
              ? formatBoundForReview(applicantPersonId, field.fieldId)
              : formatValueForDisplay(field.value),
        })),
      }));
      const person = store.persons.get(applicantPersonId);
      const participation = participationFor(applicantPersonId);
      const review: RenderedResponse = {
        applicantPersonId,
        competitionId,
        submissionStatus: response.submissionStatus,
        isDraft: response.submissionStatus === 'DRAFT',
        sections,
        reviewReturn: response.reviewReturn ?? false,
        applicantName: person
          ? `${person.legalFirstName} ${person.legalLastName}`.trim()
          : null,
        profilePhotoS3Key: person?.profilePhotoS3Key ?? null,
        divisionKey: participation?.divisionKey ?? null,
        titlePreferences: response.titlePreferences ?? [],
        approvedTitle: participation?.approvedTitle ?? null,
        // The director-gated review envelope is the single carrier of BOTH the
        // contestant-visible return note and the director-only internal reject
        // note (application-decision-feedback R5.4), mirroring the server's
        // getRenderedResponseForReview envelope.
        returnFeedbackNote: response.returnFeedbackNote ?? null,
        internalRejectNote: response.internalRejectNote ?? null,
      };
      return delay(review);
    },

    setApprovedTitle(competitionId: string, applicantPersonId: string, title: string) {
      void competitionId;
      participationFor(applicantPersonId).approvedTitle = title;
      return delay(undefined as void);
    },

    reviewApplicationResponse(
      competitionId: string,
      applicantPersonId: string,
      decision: ApplicationReviewDecision,
      note?: string,
    ) {
      const response = store.responses.get(responseKey(applicantPersonId, competitionId));
      if (!response) throw new Error(`No response for ${applicantPersonId} in ${competitionId}.`);
      // Route the incoming note to exactly one persisted field via the SAME pure
      // function the Application_Form_Service runs. Assigning BOTH fields from the
      // routed result replaces the note fields wholesale, so the field the
      // decision does not target is always cleared and a stale note from a prior
      // decision never survives (application-decision-feedback R4, R4.4). REQUEST_
      // CHANGES → returnFeedbackNote; REJECT → internalRejectNote; APPROVE/REOPEN
      // clear both.
      const notes = routeDecisionNote(decision, note);
      const applyNotes = () => {
        response.returnFeedbackNote = notes.returnFeedbackNote;
        response.internalRejectNote = notes.internalRejectNote;
      };
      if (decision === 'REOPEN') {
        if (response.submissionStatus !== 'APPROVED' && response.submissionStatus !== 'REJECTED') {
          throw new Error(
            `Only an APPROVED or REJECTED application can be reopened; this one is ${response.submissionStatus}.`,
          );
        }
        response.submissionStatus = 'SUBMITTED';
        response.reviewReturn = false;
        applyNotes();
        return delay(undefined as void);
      }
      if (response.submissionStatus !== 'SUBMITTED') {
        throw new Error(
          `An application in ${response.submissionStatus} cannot be reviewed; only a SUBMITTED application can be decided.`,
        );
      }
      if (decision === 'APPROVE') {
        response.submissionStatus = 'APPROVED';
        response.reviewReturn = false;
      } else if (decision === 'REJECT') {
        response.submissionStatus = 'REJECTED';
        response.reviewReturn = false;
      } else {
        response.submissionStatus = 'DRAFT';
        response.reviewReturn = true;
      }
      applyNotes();
      return delay(undefined as void);
    },

    setPageApproval(input: SetPageApprovalInput) {
      const key = approvalKey(input.applicantPersonId, input.competitionId, input.formPageId);
      const existing = store.pageApprovals.get(key);
      const fromStatus = existing?.status ?? DEFAULT_PAGE_APPROVAL_STATUS;
      // The shared lock-on-approve state machine decides legality + details (Req 21).
      const result = applyTransition(
        fromStatus,
        input.status,
        input.approvalDetails as ApprovalDetails | undefined,
      );
      const approval: PageApproval = {
        applicantPersonId: input.applicantPersonId,
        competitionId: input.competitionId,
        formPageId: input.formPageId,
        status: result.status,
        approvalDetails:
          result.details ?? existing?.approvalDetails ?? null,
      };
      store.pageApprovals.set(key, approval);
      return delay(approval);
    },

    // --- Manual lock override (director; season-phase-deadlines Req 16, 18) --
    setApplicationLockOverride(
      competitionId: string,
      applicantPersonId: string,
      override: ManualLockOverride | null,
    ) {
      const key = responseKey(applicantPersonId, competitionId);
      const existing = store.responses.get(key);
      // The override rides the existing (applicantPersonId, competitionId)
      // Application item on the server; when the mock has no response yet, mint a
      // minimal DRAFT record so a Director can still lock/unlock the Application
      // (Req 16.6, 18.5, 18.6). A null override clears it (date-governed).
      const base: ApplicationResponse = existing ?? {
        applicantPersonId,
        competitionId,
        formSchemaId: MOCK_FORM_SCHEMA_ID,
        formVersionId: activeVersion()?.id ?? `${MOCK_FORM_SCHEMA_ID}#v1`,
        submissionStatus: 'DRAFT',
        values: {},
        titlePreferences: null,
        manualLockOverride: null,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      };
      const next: ApplicationResponse = {
        ...base,
        manualLockOverride: override ?? null,
        updatedAt: nowIso(),
      };
      store.responses.set(key, next);
      // Echo the override + the server-derived lock/phase fields (Req 16, 17).
      return delay(withLockFields(next));
    },

    setApplicationLockOverridesForCompetition(
      competitionId: string,
      override: ManualLockOverride | null,
    ) {
      // Apply the same override uniformly to every Application in the listed
      // scope; a repeat call is idempotent (Req 18.3, 18.4). A null clears each.
      const updated: ApplicationResponse[] = [];
      for (const [key, resp] of store.responses.entries()) {
        if (resp.competitionId !== competitionId) continue;
        const next: ApplicationResponse = {
          ...resp,
          manualLockOverride: override ?? null,
          updatedAt: nowIso(),
        };
        store.responses.set(key, next);
        updated.push(withLockFields(next));
      }
      return delay(updated);
    },

    // --- Approval-detail judge visibility (Req 22.7, 21.8) -----------------
    getApprovalDetailVisibility(competitionId: string) {
      const configs: ApprovalDetailVisibilityConfig[] = [];
      for (const config of store.approvalDetailVisibility.values()) {
        if (config.competitionId === competitionId) configs.push(config);
      }
      return delay(configs);
    },

    setApprovalDetailVisibility(input: ApprovalDetailVisibilityInput) {
      // Normalize the configured keys: trim, drop blanks, dedupe (case-sensitive,
      // first occurrence wins) while preserving the director's chosen order.
      const seen = new Set<string>();
      const judgeVisibleKeys: string[] = [];
      for (const raw of input.judgeVisibleKeys) {
        const key = typeof raw === 'string' ? raw.trim() : '';
        if (key.length === 0 || seen.has(key)) continue;
        seen.add(key);
        judgeVisibleKeys.push(key);
      }
      const config: ApprovalDetailVisibilityConfig = {
        competitionId: input.competitionId,
        formPageId: input.formPageId,
        judgeVisibleKeys,
      };
      store.approvalDetailVisibility.set(
        approvalVisibilityKey(input.competitionId, input.formPageId),
        config,
      );
      return delay(config);
    },

    // --- Merit projection (Req 21.8, 22.4) ---------------------------------
    getJudgeProjection(competitionId: string, applicantPersonId: string) {
      const response = store.responses.get(responseKey(applicantPersonId, competitionId));
      const values: ResponseValues = response?.values ?? {};
      const version =
        (response && store.versions.find((v) => v.id === response.formVersionId)) ??
        activeVersion()!;
      const approvals: { formPageId: string; details?: ApprovalDetails }[] = [];
      for (const approval of store.pageApprovals.values()) {
        if (approval.applicantPersonId !== applicantPersonId) continue;
        if (approval.competitionId !== competitionId) continue;
        approvals.push({
          formPageId: approval.formPageId,
          details: (approval.approvalDetails ?? undefined) as ApprovalDetails | undefined,
        });
      }
      const viewerDivisionKey = participationFor(applicantPersonId).divisionKey ?? null;
      // Judge-audience, division-visible content + configured approval details,
      // PII never widened (Req 22.4–22.8) — the exact server projection.
      const sections = projectForJudge(
        pureSnapshotOf(version),
        values,
        judgeSourcesFor(applicantPersonId),
        approvals,
        { includePii: false, viewerDivisionKey },
      );
      return delay(sections as unknown as ApplicationSection[]);
    },
  };
}

/** The shared mock client instance used by the app in mock mode. */
export const mockApplicationFormClient: ApplicationFormClient = createMockApplicationFormClient();

/**
 * Reset the shared mock client to a fresh seeded store. The exported
 * `mockApplicationFormClient` keeps the same object identity (so any holder of
 * the selected client stays valid), but every method is rebound to a new
 * in-memory store. Intended for test `beforeEach` so cases don't bleed mutable
 * state into one another (mirrors `resetMockMeritClient`).
 */
export function resetMockApplicationFormClient(): void {
  Object.assign(mockApplicationFormClient, createMockApplicationFormClient());
}
